import type { SyntheticEvent } from 'react'
import { ItemStatus, ImageSize, ImageRatio, StatusTone, VideoProvider } from '@/constants'
import { parseVideoUrl, type ParsedVideo } from '@/utils/video'
import { getItemImageUrl, getCampaignProductImageUrl, getPlaceholderImageUrl, setFallbackImageUrl } from '@utils/image'
import {
  getItemFulfillmentStatus,
  getItemFulfillmentStatusClass,
  getItemStatus,
  getPaymentStatus,
  getPaymentStatusClass,
  showFulfillment,
} from '@utils'

const STATUS_TONES: Record<number, StatusTone> = {
  [ItemStatus.Active]: StatusTone.Active,
  [ItemStatus.Trial]: StatusTone.Active,
  [ItemStatus.Pending]: StatusTone.Processing,
  [ItemStatus.Paused]: StatusTone.Processing,
  [ItemStatus.RecycleBilling]: StatusTone.Processing,
  [ItemStatus.Cancelled]: StatusTone.Inactive,
  [ItemStatus.Replaced]: StatusTone.Inactive,
  [ItemStatus.ThirdPartyCancel]: StatusTone.Inactive,
  [ItemStatus.Complete]: StatusTone.Inactive,
  [ItemStatus.Refunded]: StatusTone.Inactive,
  [ItemStatus.Declined]: StatusTone.Inactive,
  [ItemStatus.RecycleBillingFailed]: StatusTone.Inactive,
  [ItemStatus.RecycleFailedHold]: StatusTone.Inactive,
}

const CLASS_TONES: Record<string, StatusTone> = {
  active: StatusTone.Active,
  pending: StatusTone.Processing,
  processing: StatusTone.Processing,
  cancelled: StatusTone.Inactive,
  refund: StatusTone.Inactive,
}

export function resolveItemStatusBadge(
  item: any,
  statusLabels: Record<string, string> = {}
): { label: string; tone: StatusTone } {
  return {
    label: getItemStatus(item, statusLabels, false),
    tone: STATUS_TONES[item?.status] ?? StatusTone.Neutral,
  }
}

export function resolvePaymentStatusBadge(order: any): { label: string; tone: StatusTone } {
  return { label: getPaymentStatus(order), tone: CLASS_TONES[getPaymentStatusClass(order)] ?? StatusTone.Neutral }
}

// Cancelling a subscription does not cancel a shipment already in flight, so the
// status is reported whenever showFulfillment allows it — as the support portal does.
export function resolveFulfillmentBadge(item: any): { label: string; tone: StatusTone } | null {
  if (!item || !showFulfillment(item)) {
    return null
  }
  return {
    label: getItemFulfillmentStatus(item),
    tone: CLASS_TONES[getItemFulfillmentStatusClass(item)] ?? 'processing',
  }
}

// Order items resolve via variant → campaign_product → product default; a
// campaign_product passed directly (cross-sell) resolves via its own image.
export function resolveProductImage(source: any, useSubscription = false, size: ImageSize = ImageSize.Large): string {
  const placeholder = getPlaceholderImageUrl(size)
  if (!source) {
    return placeholder
  }
  const asItem = () => getItemImageUrl(source, size, useSubscription)
  const asCampaignProduct = () => getCampaignProductImageUrl(source, size, source.product ?? null)

  // Try the other resolver before settling for the placeholder.
  const isItem = !!(source.campaign_product || source.variant || source.next_cycle_product || source.order)
  const primary = isItem ? asItem() : asCampaignProduct()
  if (primary && primary !== placeholder) {
    return primary
  }
  const secondary = isItem ? asCampaignProduct() : asItem()
  return secondary && secondary !== placeholder ? secondary : placeholder
}

// onError for product <img>: falls back to the `original` size, then a placeholder.
export function handleProductImageError(
  e: SyntheticEvent<HTMLImageElement>,
  source: any,
  useSubscription = false
): void {
  const isItem = !!(source?.campaign_product || source?.variant || source?.next_cycle_product)
  setFallbackImageUrl(e.nativeEvent, source, ImageSize.Medium, isItem, useSubscription)
}

export function resolveProductName(source: any): string {
  return (
    source?.name ||
    source?.campaign_product?.name ||
    source?.campaign_product?.product?.name ||
    source?.product?.name ||
    ''
  )
}

// Normalize a backend-supplied link to an absolute URL — a bare "youtube.com"
// must not resolve relative to the current portal path.
const BANNER_ASPECT: Record<string, number> = {
  [ImageRatio.SixteenNine]: 16 / 9,
  [ImageRatio.FourThree]: 4 / 3,
  [ImageRatio.OneOne]: 1,
}

export function resolveBannerAspect(ratio: string | undefined): number | undefined {
  return ratio ? BANNER_ASPECT[ratio] : undefined
}

export function toExternalUrl(url: string | undefined): string | undefined {
  if (!url || !url.trim()) {
    return undefined
  }
  const trimmed = url.trim()
  if (/^(https?:\/\/|\/\/|\/|mailto:|tel:|#)/i.test(trimmed)) {
    return trimmed
  }
  return `https://${trimmed}`
}

// ---- Video embeds ----

// parseVideoUrl / ParsedVideo now live in the shared @/utils/video so step media can reuse them.
export { parseVideoUrl }
export type { ParsedVideo }

interface VideoEmbedOptions {
  autoplay?: boolean
  loop?: boolean
  controls?: boolean
}

// iframe src for an embed provider — YouTube via the nocookie host, muted when autoplaying; undefined for a File.
export function videoEmbedUrl(video: ParsedVideo | undefined, options: VideoEmbedOptions = {}): string | undefined {
  if (!video) {
    return undefined
  }
  const autoplay = options.autoplay === true
  const loop = options.loop === true
  const controls = options.controls !== false
  if (video.provider === VideoProvider.YouTube) {
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      mute: autoplay ? '1' : '0',
      controls: controls ? '1' : '0',
      loop: loop ? '1' : '0',
      rel: '0',
      playsinline: '1',
    })
    // YouTube ignores loop on a single video unless the playlist is that same video.
    if (loop) {
      params.set('playlist', video.id)
    }
    return `https://www.youtube-nocookie.com/embed/${video.id}?${params.toString()}`
  }
  if (video.provider === VideoProvider.Vimeo) {
    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      muted: autoplay ? '1' : '0',
      loop: loop ? '1' : '0',
      controls: controls ? '1' : '0',
      dnt: '1',
    })
    return `https://player.vimeo.com/video/${video.id}?${params.toString()}`
  }
  return undefined
}

// Plain-URL provider thumbnail (YouTube only); Vimeo uses fetchVimeoThumbnail, files their first frame.
export function videoThumbnailUrl(video: ParsedVideo | undefined): string | undefined {
  if (!video || video.provider !== VideoProvider.YouTube) {
    return undefined
  }
  return `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`
}

const VIMEO_THUMBNAIL_TIMEOUT_MS = 5000

// Fetches Vimeo's thumbnail from its CORS-enabled oEmbed endpoint; undefined for non-Vimeo or on failure.
export async function fetchVimeoThumbnail(video: ParsedVideo | undefined): Promise<string | undefined> {
  if (!video || video.provider !== VideoProvider.Vimeo) {
    return undefined
  }
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), VIMEO_THUMBNAIL_TIMEOUT_MS)
  try {
    const endpoint = `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${video.id}`
    const response = await fetch(endpoint, { signal: controller.signal })
    if (!response.ok) {
      return undefined
    }
    const data = await response.json()
    return typeof data?.thumbnail_url === 'string' ? data.thumbnail_url : undefined
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}

// sections_config pairs a hex color with a 0-100 opacity in a separate key.
export function colorWithOpacity(color: string | undefined, percent: number | undefined): string | undefined {
  if (!color || !color.trim()) {
    return undefined
  }
  const value = Number(percent)
  if (!Number.isFinite(value) || value >= 100) {
    return color
  }
  return `color-mix(in srgb, ${color} ${Math.max(0, value)}%, transparent)`
}

export function ctaVisible(data: { cta_enabled?: boolean; cta_label?: string }): boolean {
  if (data.cta_enabled == null) {
    return !!data.cta_label
  }
  return data.cta_enabled && !!data.cta_label
}

// Strips inline text colour so the nav's colour applies; keeps background-color.
export function stripHtmlTextColor(html: string | undefined): string | undefined {
  if (!html) {
    return html
  }
  return html.replace(/\sstyle=(["'])(.*?)\1/gi, (_match, quote: string, css: string) => {
    const kept = css
      .split(';')
      .filter(declaration => declaration.trim() && !/^\s*color\s*:/i.test(declaration))
      .join(';')
    return kept ? ` style=${quote}${kept}${quote}` : ''
  })
}

export function hasVisibleHtml(html: string | undefined): boolean {
  if (!html) {
    return false
  }
  if (/<(img|video|iframe|svg)\b/i.test(html)) {
    return true
  }
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
    .replace(/[\s\u00a0\u200b]+/g, '')
  return text.length > 0
}

// The Welcome Message has no alignment setting — the editor bakes it into the
// saved rich text, and it positions the message against the credits balance.
export function readHtmlAlignment(html: string | undefined): 'left' | 'center' | 'right' {
  if (!html) {
    return 'left'
  }
  const styled = html.match(/text-align\s*:\s*(left|center|right)/i)
  if (styled) {
    return styled[1].toLowerCase() as 'left' | 'center' | 'right'
  }
  const attr = html.match(/align\s*=\s*["']?(left|center|right)/i)
  if (attr) {
    return attr[1].toLowerCase() as 'left' | 'center' | 'right'
  }
  return 'left'
}

export function resolveCompareAtPrice(item: any): number | undefined {
  const raw = item?.variant?.retail_price ?? item?.retail_price
  if (raw == null) {
    return undefined
  }
  const value = Number(raw)
  return Number.isNaN(value) ? undefined : value
}

export function resolveIntervalLabel(sub: any): string | undefined {
  const days = sub?.billing_interval_days ?? sub?.recurring_days
  if (typeof days === 'number' && days > 0) {
    return `Every ${days} Days`
  }
  return undefined
}

// "Delivering to" uses the session customer's shipping address.
export function resolveDeliveryLine(customer: any): string | undefined {
  return customer?.ship_address1 || undefined
}

export function formatLongDate(iso: string | undefined): string {
  if (!iso) {
    return ''
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
}

export function sessionTokens(session: any, extra: Record<string, string> = {}): Record<string, string> {
  const customer = session?.customer
  const first = customer?.first_name || customer?.ship_first_name || ''
  const last = customer?.last_name || customer?.ship_last_name || ''
  return {
    first_name: first,
    last_name: last,
    full_name: [first, last].filter(Boolean).join(' '),
    brand_name: session?.campaign?.display_name || session?.campaign?.name || '',
    ...extra,
  }
}

export function interpolateTemplate(template: string | undefined, vars: Record<string, string | undefined>): string {
  if (!template) {
    return ''
  }
  // An unresolved token is left as-is rather than blanked — a gap reads as a broken
  // sentence, the raw token reads as missing data.
  return template.replace(/\{([^}]+)\}/g, (match, token: string) => {
    const value = vars[token.trim()]
    return value ? value : match
  })
}
